/* eslint-disable */
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
    Typography, Divider, useMediaQuery, Collapse
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LogoSection from 'layout/MainLayout/LogoSection';

export const HR_DRAWER_WIDTH = 240;

// ── HR-specific navigation items ─────────────────────────────────────────────
const HR_MENU = [
    {
        id: 'expenses',
        title: 'Office Expenses',
        icon: AccountBalanceWalletIcon,
        url: '/hr/expenses',
        children: [
            {
                id: 'all-expenses',
                title: 'All Expenses',
                icon: ReceiptLongIcon,
                url: '/hr/expenses',
            },
        ],
    },
];

// ── Single nav item ───────────────────────────────────────────────────────────
function HrNavItem({ item, depth = 0 }) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [open, setOpen] = useState(true);

    const hasChildren = item.children && item.children.length > 0;
    const isActive = pathname === item.url || pathname.startsWith(item.url + '/');
    const Icon = item.icon;

    const handleClick = () => {
        if (hasChildren) {
            setOpen((p) => !p);
        } else {
            navigate(item.url);
        }
    };

    return (
        <>
            <ListItemButton
                onClick={handleClick}
                sx={{
                    pl: 2 + depth * 2,
                    pr: 2,
                    py: 1,
                    mb: 0.5,
                    borderRadius: 2,
                    // Active state: light blue background, black text
                    bgcolor: isActive && !hasChildren ? '#e8f0fe' : 'transparent',
                    color: isActive && !hasChildren ? '#000000' : '#1a1a1a',
                    '&:hover': {
                        bgcolor: isActive && !hasChildren ? '#d4e4fc' : '#f5f5f5',
                    },
                    transition: 'all 0.2s',
                }}
            >
                <ListItemIcon
                    sx={{
                        minWidth: 36,
                        color: isActive && !hasChildren ? '#114CC2' : '#114CC2',
                    }}
                >
                    <Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{
                        fontSize: depth === 0 ? '0.875rem' : '0.8rem',
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? '#000000' : '#1a1a1a',
                    }}
                />
                {hasChildren && (open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />)}
            </ListItemButton>

            {hasChildren && (
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <List disablePadding>
                        {item.children.map((child) => (
                            <HrNavItem key={child.id} item={child} depth={depth + 1} />
                        ))}
                    </List>
                </Collapse>
            )}
        </>
    );
}

// ── Drawer content ────────────────────────────────────────────────────────────
function DrawerContent() {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#ffffff' }}>

            {/* HR label chip – subtle blue */}
            <Box sx={{ px: 2, pb: 1 }}>
                <Box
                    sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        bgcolor: '#e8f0fe', borderRadius: 2, px: 1.5, py: 0.5,
                    }}
                >
                </Box>
            </Box>

            <Divider sx={{ mb: 1, borderColor: '#e0e0e0' }} />

            {/* Navigation */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 1 }}>
                <List disablePadding>
                    {HR_MENU.map((item) => (
                        <HrNavItem key={item.id} item={item} />
                    ))}
                </List>
            </Box>

            <Divider sx={{ borderColor: '#e0e0e0' }} />
            <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="caption" color="#666666">
                    HR Portal © {new Date().getFullYear()}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function HrSidebar({ drawerOpen, drawerToggle }) {
    const theme = useTheme();
    const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));

    return (
        <Box
            component="nav"
            sx={{
                width: matchUpMd && drawerOpen ? HR_DRAWER_WIDTH : 0,
                flexShrink: 0,
                transition: 'width 0.25s ease',
            }}
        >
            <Drawer
                variant={matchUpMd ? 'persistent' : 'temporary'}
                anchor="left"
                open={drawerOpen}
                onClose={drawerToggle}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: HR_DRAWER_WIDTH,
                        bgcolor: '#ffffff',          // pure white background
                        borderRight: '1px solid #e8e8e8',
                        [theme.breakpoints.up('md')]: { top: '64px' },
                        boxShadow: 'none',           // remove shadow for cleaner look
                    },
                }}
                ModalProps={{ keepMounted: true }}
            >
                <DrawerContent />
            </Drawer>
        </Box>
    );
}